import React, { useState } from 'react';

import { Box, Button, Checkbox, Divider, FormControlLabel, Stack, Switch, TextField } from '@mui/material';

import ReservationService from '~/services/ReservationService';

import { formatValidationMessages } from '~/utils/formatValidationMessages';
import { reportError } from '~/utils/reportError';

import ErrorListDisplay from '../ErrorListDisplay';

import { buttonMessages, buttonStates } from './states/submitButton';

interface Props {
  propertyId: string;
  roomType: any;
  roomRate: any;
  capacity?: any;
  onRoomTypeCapacityUpdated: (result: any) => void;
  onCancel: () => void;
}

interface FormState {
  maxOccupancy: number;
  maxAdultOccupancy: number;
  maxChildOccupancy: number;
  maxInfantOccupancy: number;
  maxTeenagerOccupancy: number;
  includeInfants: boolean;
}

export default function RoomTypeOccupancyForm({
  propertyId,
  roomType,
  roomRate,
  onRoomTypeCapacityUpdated,
  onCancel,
}: Props) {
  const [formState, setFormState] = useState<FormState>({
    maxOccupancy: roomType.max_occupancy,
    maxAdultOccupancy: roomType.max_adult_occupancy,
    maxChildOccupancy: roomType.max_child_occupancy,
    maxInfantOccupancy: roomType.max_infant_occupancy,
    maxTeenagerOccupancy: roomType.max_teenager_occupancy,
    includeInfants: false,
  });

  const [saveState, setSaveState] = useState(buttonStates.default);
  const [errors, setErrors] = useState(undefined);
  const [showAsGuests, setShowAsGuests] = useState(false);

  const handleSubmit = async () => {
    if (!window.confirm('This will regenerate all capacities, are you sure?')) {
      return;
    }

    setSaveState(buttonStates.saving);
    setErrors(null);

    const data = {
      max_occupancy: formState.maxOccupancy,
      max_adult_occupancy: formState.maxAdultOccupancy,
      max_child_occupancy: formState.maxChildOccupancy,
      max_infant_occupancy: formState.maxInfantOccupancy,
      max_teenager_occupancy: formState.maxTeenagerOccupancy,
      include_infants: formState.includeInfants,
    };

    try {
      const response = await ReservationService.updateRoomTypeOccupancy(data, propertyId, roomType.id, roomRate.id);
      setSaveState(buttonStates.saved);
      setErrors(null);
      onRoomTypeCapacityUpdated(response.result);
    } catch (error) {
      const validationErrors = error.name === 'ValidationError' ? error.errors : null;
      setSaveState(buttonStates.failed);
      setErrors(validationErrors);
      reportError(error);
    }
  };

  const handleValueChange = (fieldName: keyof FormState, value: string | boolean) => {
    setFormState((prev) => ({
      ...prev,
      [fieldName]: typeof value === 'boolean' ? value : parseInt(value) || 0,
    }));
  };

  const formRow = (label: string, fieldName: keyof FormState) => (
    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
      <Box sx={{ flex: 1 }}>{label}</Box>
      <Box sx={{ flex: 1 }}>
        <TextField
          type="number"
          inputProps={{ min: 0, step: 1 }}
          value={formState[fieldName]}
          onChange={(e) => handleValueChange(fieldName, e.target.value)}
          fullWidth
          size="small"
        />
      </Box>
    </Box>
  );

  return (
    <Box sx={{ p: 1 }}>
      {formRow('Total number of guests', 'maxOccupancy')}

      <Divider sx={{ my: 2 }} />

      <FormControlLabel
        control={<Switch checked={showAsGuests} onChange={(e) => setShowAsGuests(e.target.checked)} size="small" />}
        label="Display 'Adults' as 'Guests'"
        sx={{ mb: 2 }}
      />

      {formRow(showAsGuests ? 'Guests' : 'Adults', 'maxAdultOccupancy')}
      {formRow('Children', 'maxChildOccupancy')}
      {formRow('Infants', 'maxInfantOccupancy')}
      {formRow('Teenagers', 'maxTeenagerOccupancy')}

      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
        <Box sx={{ flex: 1 }}>Include infants</Box>
        <Box sx={{ flex: 1 }}>
          <Checkbox
            checked={formState.includeInfants}
            onChange={(e) => handleValueChange('includeInfants', e.target.checked)}
            size="small"
          />
        </Box>
      </Box>

      <Divider sx={{ my: 2 }} />
      {errors && (
        <Box sx={{ mb: 2 }}>
          <ErrorListDisplay messages={formatValidationMessages(errors)} />
        </Box>
      )}

      <Stack direction="row" justifyContent="space-between" spacing={2}>
        <Button variant="text" onClick={onCancel}>
          Cancel
        </Button>
        <Button variant="contained" type="submit" disabled={saveState === buttonStates.saving} onClick={handleSubmit}>
          {buttonMessages[saveState]}
        </Button>
      </Stack>
    </Box>
  );
}
